import { useMemo } from "react";

/**
 * Selettore del periodo della Reportistica, in tre gradini che si stringono:
 *
 *   1. l'ANNO            — una tendina, solo gli anni che hanno dati
 *   2. l'INTERVALLO DI MESI — uno slider a due maniglie (da agosto a ottobre)
 *   3. le SETTIMANE      — i mesi scelti si aprono nelle loro settimane, che si
 *                          accendono e spengono una per una
 *
 * Sostituisce la vecchia coppia "tendina mese + bottoni settimana", che permetteva
 * di guardare una settimana sola o un mese solo. Qui il periodo puo' attraversare
 * piu' mesi, che e' quello che serve per confrontare periodi lunghi.
 *
 * Lo slider e' fatto con due <input type="range"> sovrapposti: e' il modo che
 * funziona da tastiera senza scrivere niente, e non aggiunge una dipendenza al
 * progetto per un solo componente. Le maniglie non possono scavalcarsi.
 *
 * I dodici mesi sono SEMPRE tutti sull'asse, anche quelli senza dati: uno slider
 * che cambia lunghezza a seconda di cosa e' stato caricato non si legge. I mesi
 * vuoti restano attraversabili e semplicemente non portano settimane.
 */

const MESI_BREVI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

/** "2026-08" -> 2026 / 7 (indice del mese, 0-based). */
export const annoDi = (idMese) => Number(String(idMese).slice(0, 4));
export const indiceMeseDi = (idMese) => Number(String(idMese).slice(5, 7)) - 1;

const SelettorePeriodo = ({
    mesi,                 // [{id, label, settimane[]}] dal payload
    settimane,            // mappa id -> settimana, per le etichette
    anno, onAnno,
    range, onRange,       // [da, a] indici 0-11
    settimaneSpente,      // Set di id spenti dall'utente
    onToggleSettimana,
}) => {
    const anni = useMemo(
        () => [...new Set(mesi.map((m) => annoDi(m.id)))].sort((a, b) => b - a),
        [mesi]
    );

    // Quali dei dodici mesi hanno davvero dati, per l'anno scelto.
    const conDati = useMemo(() => {
        const s = new Set();
        for (const m of mesi) if (annoDi(m.id) === anno) s.add(indiceMeseDi(m.id));
        return s;
    }, [mesi, anno]);

    /* Le settimane dei mesi dentro l'intervallo, in ordine di calendario e SENZA
       ripetizioni: una settimana a cavallo (31 ago - 5 set) appartiene a due mesi,
       e selezionandoli entrambi arriverebbe due volte. */
    const settimaneDelRange = useMemo(() => {
        const [da, a] = range;
        const ids = mesi
            .filter((m) => annoDi(m.id) === anno && indiceMeseDi(m.id) >= da && indiceMeseDi(m.id) <= a)
            .flatMap((m) => m.settimane);
        return [...new Set(ids)].sort();
    }, [mesi, anno, range]);

    const [da, a] = range;
    // Percentuali per disegnare la barra piena fra le due maniglie.
    const sx = (da / 11) * 100;
    const dx = (a / 11) * 100;

    const accese = settimaneDelRange.filter((id) => !settimaneSpente.has(id)).length;

    return (
        <div className="gp-per">
            <div className="gp-per-riga">
                <label className="gp-per-lab" htmlFor="gp-per-anno">Anno</label>
                <select id="gp-per-anno" className="gp-per-anno" value={anno}
                    onChange={(e) => onAnno(Number(e.target.value))}>
                    {anni.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>

                <div className="gp-per-slider">
                    {/* Le due tacche estreme dell'intervallo, scritte per esteso: lo
                        slider da solo si legge male a colpo d'occhio. */}
                    <div className="gp-per-estremi">
                        <strong>{MESI_BREVI[da]}</strong>
                        {da !== a && <> – <strong>{MESI_BREVI[a]}</strong></>}
                        <span className="gp-per-conta">
                            {settimaneDelRange.length === 0
                                ? "nessuna settimana con dati"
                                : `${accese} di ${settimaneDelRange.length} settiman${settimaneDelRange.length === 1 ? "a" : "e"}`}
                        </span>
                    </div>

                    <div className="gp-per-track">
                        <div className="gp-per-fill" style={{ left: `${sx}%`, right: `${100 - dx}%` }} />
                        {/* i mesi senza dati restano visibili ma smorzati */}
                        {MESI_BREVI.map((_, i) => (
                            <span key={i}
                                className={`gp-per-tick ${conDati.has(i) ? "ha-dati" : ""} ${i >= da && i <= a ? "dentro" : ""}`}
                                style={{ left: `${(i / 11) * 100}%` }} />
                        ))}
                        <input type="range" min="0" max="11" value={da} aria-label="Primo mese del periodo"
                            className="gp-per-range gp-per-range-da"
                            onChange={(e) => onRange([Math.min(Number(e.target.value), a), a])} />
                        <input type="range" min="0" max="11" value={a} aria-label="Ultimo mese del periodo"
                            className="gp-per-range gp-per-range-a"
                            onChange={(e) => onRange([da, Math.max(Number(e.target.value), da)])} />
                    </div>

                    <div className="gp-per-scala">
                        {MESI_BREVI.map((m, i) => (
                            <span key={m} className={`${i >= da && i <= a ? "dentro" : ""} ${conDati.has(i) ? "" : "vuoto"}`}>{m}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Terzo gradino: le settimane dei mesi scelti, accendibili una per una. */}
            {settimaneDelRange.length > 0 && (
                <div className="gp-per-sett">
                    <span className="gp-per-lab">Settimane</span>
                    <div className="gp-per-chip">
                        {settimaneDelRange.map((id) => {
                            const on = !settimaneSpente.has(id);
                            return (
                                <button key={id} type="button"
                                    className={`gp-rep-seg-btn gp-per-w ${on ? "active" : ""}`}
                                    aria-pressed={on}
                                    onClick={() => onToggleSettimana(id)}
                                    title={on ? "Escludi questa settimana" : "Includi questa settimana"}>
                                    <span className="gp-rep-seg-num">{settimane[id].num}</span>
                                    <span className="gp-rep-seg-gg">{settimane[id].giorniLabel}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelettorePeriodo;
